import NoData from "@/components/ui/no-data";
import { SOCIALS } from "@/constants/socials";
import { authStore } from "@/lib/auth/store";
import { request } from "@/lib/request";
import { BaseResponse } from "@/lib/request";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import { Creation, CreationList } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { DiscordIcon, TelegramIcon, XIcon } from "~/components/icons";

function getHistoryList() {
	return request.get("/aifk/creation/visit_history");
}

export function Sidebar() {
	const [open, setOpen] = useState(true);
	const user = useStore(authStore, (s) => s.user);
	const router = useRouter();
	const { data: _historyList = [] } = useQuery<
		BaseResponse<CreationList>,
		Error,
		Creation[]
	>({
		queryKey: ["aifk/creation/visit_history"],
		queryFn: () => getHistoryList(),
		select: (data) => data.data.list || [],
		enabled: !!user,
	});

	const SOCIAL_ICONS = {
		X: <XIcon className="w-5 h-5 text-t3 [&>path]:fill-t3" />,
		Discord: <DiscordIcon className="w-5 h-5 text-t3 [&>path]:fill-t3" />,
		Telegram: <TelegramIcon className="w-5 h-5 text-t3 [&>path]:fill-t3" />,
	};

	const isHide = useMemo(() => {
		const pathname = router.latestLocation.pathname;
		return pathname.includes("/edit/") || pathname.includes("/profile/");
	}, [router.latestLocation.pathname]);

	// SOCIALS animation layout
	const iconSize = 24; // px
	const gap = 3; // px
	const socialsLayout = open
		? SOCIALS.map((_, idx) => ({ x: idx * (iconSize + gap), y: 0 }))
		: SOCIALS.map((_, idx) => ({ x: 0, y: idx * (iconSize + gap) }));
	const socialsContainerWidth = open
		? SOCIALS.length * iconSize + (SOCIALS.length - 1) * gap
		: iconSize - 12;
	const socialsContainerHeight = open
		? iconSize
		: SOCIALS.length * iconSize + (SOCIALS.length - 1) * gap + 24;

	return (
		<div
			className={cn(
				"flex mt-9 flex-col ml-10 justify-between items-center h-[80vh] bg-card rounded-[30px] shadow-card py-2.5 transition-all duration-500",
				open ? "w-[110px] min-w-[110px] pl-4" : "w-[50px] min-w-[50px]",
				isHide && "hidden",
			)}
		>
			{/* 顶部按钮 */}
			<div
				className={cn(
					"flex flex-col w-full",
					open ? "items-end" : "items-center",
				)}
			>
				<button
					type="button"
					className={cn(
						"bg-bg1 cursor-pointer w-9.5 h-9.5 mb-3.5 rounded-full flex items-center justify-center transition-all duration-500",
						open && "mr-1",
					)}
					onClick={() => setOpen((v) => !v)}
				>
					{open ? (
						<ChevronLeft className="w-5 h-5 text-t3" />
					) : (
						<ChevronRight className="w-5 h-5 text-t3" />
					)}
				</button>
			</div>

			<div className={cn("flex-1 overflow-y-auto", open ? "pr-4" : "pr-0")}>
				<p
					className={cn(
						"text-t3 text-sm text-center mb-3 transition-all duration-500",
						open ? "opacity-100 h-5" : "opacity-0 h-0",
					)}
				>
					{m.play_history()}
				</p>
				{_historyList?.map((item) => (
					<Link
						preload={false}
						to={localizeHref(`/story/${item.creation_id}/-`)}
						key={item.creation_id}
						className={cn(
							"flex flex-col items-center gap-3 transition-all duration-500",
							open ? "mb-3.5" : "mb-1",
						)}
						onClick={(e) => {
							if (item.status !== "PUBLISHED") {
								e.preventDefault();
								toast.error(m.story_creation_not_found());
							}
						}}
					>
						<motion.div
							layout
							transition={{ type: "spring", stiffness: 400, damping: 40 }}
							className="flex flex-col items-center gap-2"
						>
							<motion.img
								src={item?.cover_image}
								alt={item.title}
								loading="eager"
								layout
								initial={false}
								animate={
									open
										? { width: 80, height: 80, borderRadius: 20, opacity: 1 }
										: { width: 38, height: 38, borderRadius: 999, opacity: 0.7 }
								}
								transition={{ type: "spring", stiffness: 400, damping: 40 }}
								className="object-cover"
								style={{ minWidth: open ? 80 : 38, minHeight: open ? 80 : 38 }}
							/>
							<motion.span
								initial={false}
								animate={
									open
										? { opacity: 1, height: "auto" }
										: { opacity: 0, height: 0 }
								}
								transition={{ duration: 0.4 }}
								className="text-t1 text-xs max-w-[80px] line-clamp-2 overflow-hidden break-words text-center"
							>
								{item.title}
							</motion.span>
						</motion.div>
					</Link>
				))}
				{open && _historyList?.length === 0 && (
					<NoData className="[&>svg]:max-w-14.5 [&>svg]:h-auto" />
				)}
			</div>
			{/* footer socials */}
			<div
				className={cn(
					"flex flex-col items-center w-full pt-1",
					open ? "pr-4" : "",
				)}
			>
				<span
					className={cn(
						"text-t3 text-sm mb-1.5 transition-all duration-500",
						open ? "opacity-100" : "opacity-0",
					)}
				>
					{m.contact_us()}
				</span>
				<motion.div
					className="relative bg-bg1 rounded-full p-2 flex items-center justify-center"
					style={{
						width: socialsContainerWidth + 24,
						height: socialsContainerHeight + 10,
						transition: "width 0.5s, height 0.5s",
					}}
				>
					{SOCIALS.map(
						(item, idx) =>
							item.name !== "Telegram" && (
								<motion.a
									key={item.href}
									href={item.href}
									target="_blank"
									rel="noopener noreferrer"
									initial={false}
									animate={{
										x: socialsLayout[idx].x,
										y: socialsLayout[idx].y,
										opacity: 1,
										scale: 1,
									}}
									transition={{ type: "spring", stiffness: 400, damping: 30 }}
									className="absolute left-1/2 top-1/2"
									style={{
										width: iconSize,
										height: iconSize,
										marginLeft: -iconSize / 2 - (open ? 28 - 14 : 0), // hidden Telegram add "-13"
										marginTop: -iconSize / 2 - (open ? 0 : 28 - 14),
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
									whileHover={{ scale: 1.15 }}
								>
									{SOCIAL_ICONS[item.name as keyof typeof SOCIAL_ICONS]}
								</motion.a>
							),
					)}
				</motion.div>
			</div>
		</div>
	);
}
